import React, { useState } from 'react';
import { X, Printer, Download } from 'lucide-react';
import { useOrganization } from '../../hooks/useOrganization';
import { formatCurrency } from '../../utils/currency';

const PrintablePurchaseOrder = ({ order, onClose }) => {
  const { organization, loading } = useOrganization();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    setIsPrinting(true);
    const originalTitle = document.title;
    document.title = `PO-${order.orderNo}`;
    
    // Get the printable content and fix relative image URLs to absolute
    const content = document.querySelector('.printable-content');
    const clone = content.cloneNode(true);
    
    // Fix all image src to absolute URLs
    clone.querySelectorAll('img').forEach(img => {
      const src = img.getAttribute('src');
      if (src && src.startsWith('/uploads')) {
        img.setAttribute('src', `${(window.ENV && window.ENV.REACT_APP_API_URL) || ''}${src}`);
      }
    });
    
    // Open a new window with only the printable content
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>PO-${order.orderNo}</title>
        <style>
          body { margin: 0; padding: 0.5in; font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.2; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
          .no-print { display: none !important; }
          .logo-container { text-align: center; margin-bottom: 8pt; padding: 4pt 0; border-bottom: 1pt solid #ddd; }
          .company-logo { height: 1.3in; width: 100%; object-fit: fill; display: block; margin: 0 auto; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; font-size: 9pt; }
          th { background: #f0f0f0; font-weight: bold; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-semibold, .font-bold { font-weight: bold; }
          .text-sm { font-size: 9pt; }
          .text-xs { font-size: 8pt; }
          .mb-4 { margin-bottom: 12pt; }
          .mb-3 { margin-bottom: 8pt; }
          .p-1 { padding: 4pt; }
          .bg-gray-50 { background: #f9f9f9; }
          .border-b { border-bottom: 1px solid #ddd; }
          .grid { display: grid; }
          .grid-cols-2 { grid-template-columns: 1fr 1fr; }
          .grid-cols-3 { grid-template-columns: 1fr 1fr 1fr; }
          .gap-8 { gap: 20pt; }
          .gap-4 { gap: 10pt; }
          .mt-8 { margin-top: 20pt; }
          .signature-line { height: 40px; border-bottom: 2px solid #999; margin-bottom: 4pt; }
          .print-footer { text-align: center; font-size: 7pt; color: #666; border-top: 1px solid #ddd; padding-top: 6pt; margin-top: 12pt; }
          @media print { @page { size: A4; margin: 0.5in; } body { padding: 0; } }
        </style>
      </head>
      <body>${clone.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      document.title = originalTitle;
      setIsPrinting(false);
    }, 700);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>Error: Purchase order data not available</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      {/* Print Controls - Hidden in print */}
      <div className="no-print flex justify-between items-center p-4 border-b bg-gray-50 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Purchase Order - Print View</h2>
          <p className="text-sm text-gray-600">PO #{order.orderNo} | {order.vendor?.name}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Printer className="w-4 h-4 mr-2" />
            {isPrinting ? 'Printing...' : 'Print'}
          </button>
          <button
            onClick={onClose}
            className="flex items-center px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="printable-content">
        {/* Header Section */}
        <div className="print-header mb-4">
          {/* Logo Bar */}
          <div className="logo-container mb-4">
            {organization?.poLogoUrl ? (
              <img 
                src={organization.poLogoUrl} 
                alt="PO Logo" 
                className="company-logo"
              />
            ) : organization?.logoUrl ? (
              <div className="flex justify-center">
                <img 
                  src={organization.logoUrl} 
                  alt="Company Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {organization?.companyName?.substring(0, 4).toUpperCase() || 'AGMS'}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Company & PO Info */}
          <div className="header-info grid grid-cols-3 gap-6 items-start pb-4">
            <div className="info-box">
              <h3 className="section-title">BRANCH INFORMATION</h3>
              <div className="info-grid">
                <div className="info-row"><span className="label">Branch:</span> <span className="value">{organization?.branchName || 'Main Branch'}</span></div>
                <div className="info-row"><span className="label">Phone:</span> <span className="value">{organization?.companyContact || 'N/A'}</span></div>
                <div className="info-row"><span className="label">Email:</span> <span className="value">{organization?.email || 'N/A'}</span></div>
                <div className="info-row"><span className="label">Address:</span> <span className="value" style={{whiteSpace: 'nowrap'}}>{organization?.address || '123 Main street'}, {organization?.city || 'Dubai'}, {organization?.country || 'UAE'}</span></div>
                {organization?.vatId && <div className="info-row"><span className="label">TRN:</span> <span className="value">{organization.vatId}</span></div>}
              </div>
            </div>
            
            <div className="info-box">
              <h3 className="section-title">VENDOR INFORMATION</h3>
              <div className="info-grid">
                <div className="info-row"><span className="label">Contact:</span> <span className="value">{order.vendor?.contactPerson || 'N/A'}</span></div>
                <div className="info-row"><span className="label">Phone:</span> <span className="value">{order.vendor?.phone || 'N/A'}</span></div>
                <div className="info-row"><span className="label">Email:</span> <span className="value">{order.vendor?.email || 'N/A'}</span></div>
                <div className="info-row"><span className="label">Address:</span> <span className="value">{order.vendor?.address || 'N/A'}</span></div>
                {order.vendor?.trn && <div className="info-row"><span className="label">TRN:</span> <span className="value">{order.vendor.trn}</span></div>}
              </div>
            </div>
            
            <div className="info-box">
              <h3 className="section-title">PURCHASE ORDER</h3>
              <div className="info-grid">
                <div className="info-row"><span className="label">PO Number:</span> <span className="value font-bold">#{order.orderNo}</span></div>
                <div className="info-row"><span className="label">Date:</span> <span className="value">{new Date(order.createdAt).toLocaleDateString()}</span></div>
                <div className="info-row"><span className="label">Total Qty:</span> <span className="value">{order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}</span></div>
                <div className="info-row"><span className="label">Total Amount:</span> <span className="value font-bold">{formatCurrency(order.totalAmount || 0, organization?.currency_code)}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-4" style={{marginTop: '-4pt'}}>
          <h3 className="text-base font-semibold mb-2 text-gray-800 border-b pb-1">Order Items</h3>
          <table className="w-full border-collapse border border-gray-300 items-table">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-1 text-left text-sm">#</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-sm">Item Description</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-sm">Additional Info</th>
                <th className="border border-gray-300 px-2 py-1 text-center text-sm">Qty</th>
                <th className="border border-gray-300 px-2 py-1 text-right text-sm">Unit Price</th>
                <th className="border border-gray-300 px-2 py-1 text-center text-sm">Disc %</th>
                <th className="border border-gray-300 px-2 py-1 text-right text-sm">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, index) => {
                const itemTotal = (item.quantity || 0) * (item.unitPrice || 0);
                const discountAmount = itemTotal * ((item.discount || 0) / 100);
                const finalTotal = itemTotal - discountAmount;
                return (
                  <tr key={index}>
                    <td className="border border-gray-300 px-2 py-1 text-sm">{index + 1}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm">{item.partName || `Part ID: ${item.partId}`}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm italic text-gray-600">{item.description || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-center text-sm">{item.quantity}</td>
                    <td className="border border-gray-300 px-2 py-1 text-right text-sm">{formatCurrency(item.unitPrice || 0, organization?.currency_code)}</td>
                    <td className="border border-gray-300 px-2 py-1 text-center text-sm">{item.discount || 0}%</td>
                    <td className="border border-gray-300 px-2 py-1 text-right text-sm">{formatCurrency(finalTotal, organization?.currency_code)}</td>
                  </tr>
                );
              }) || (
                <tr>
                  <td colSpan="7" className="border border-gray-300 px-2 py-4 text-center text-gray-500 text-sm">No items found</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="6" className="border border-gray-300 px-2 py-1 text-right font-medium text-sm">Subtotal:</td>
                <td className="border border-gray-300 px-2 py-1 text-center text-sm">{formatCurrency(order.subtotal || order.totalAmount || 0, organization?.currency_code)}</td>
              </tr>
              {(order.vatPercentage > 0 || order.vatAmount > 0) && (
                <tr>
                  <td colSpan="6" className="border border-gray-300 px-2 py-1 text-right font-medium text-sm">VAT ({order.vatPercentage || 0}%):</td>
                  <td className="border border-gray-300 px-2 py-1 text-center text-sm">{formatCurrency(order.vatAmount || 0, organization?.currency_code)}</td>
                </tr>
              )}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan="6" className="border border-gray-300 px-2 py-1 text-right text-sm">Total Amount:</td>
                <td className="border border-gray-300 px-2 py-1 text-center text-sm">{formatCurrency(order.totalAmount || 0, organization?.currency_code)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="mb-3 notes-section">
            <h3 className="text-base font-semibold mb-0 text-gray-800 border-b pb-0">Notes</h3>
            <p className="text-gray-700 bg-gray-50 p-1 rounded" style={{marginTop: '-2pt'}}>{order.notes}</p>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="mb-3 terms-section">
          <h3 className="text-base font-semibold mb-0 text-gray-800 border-b pb-0">Terms & Conditions</h3>
          <div className="text-xs text-gray-700 bg-gray-50 p-1 rounded" style={{fontSize: '6pt', lineHeight: '1.1', marginTop: '-2pt'}}>
            {order.termsConditions ? (
              <div className="whitespace-pre-line">{order.termsConditions}</div>
            ) : (
              <div className="space-y-1">
                <p>• Payment Terms: {order.vendor?.paymentTerms || 'Net 30 days from delivery'}</p>
                <p>• All items must be delivered as per specifications and quality standards</p>
                <p>• Quality inspection will be conducted upon delivery</p>
                <p>• Any discrepancies must be reported within 48 hours of delivery</p>
                <p>• Prices are inclusive of VAT where applicable</p>
                <p>• Delivery should be made to warehouse during business hours</p>
                <p>• Supplier must provide delivery receipt and invoice</p>
                <p>• Returns accepted only for defective items within 7 days</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Section */}
        <div className="grid grid-cols-2 gap-8 mt-8 mb-6 signature-section">
          <div className="text-center">
            <div className="h-12 border-b-2 border-gray-400 mb-2 flex items-end justify-center signature-line">
              <span className="text-xs text-gray-400 mb-1">Signature</span>
            </div>
            <p className="text-sm font-medium text-gray-700 signature-label">Authorized Manager</p>
          </div>
          <div className="text-center">
            <div className="h-12 border-2 border-dashed border-gray-300 rounded-lg mb-2 flex items-center justify-center bg-gray-50 signature-line">
              <span className="text-xs text-gray-400">Company Stamp</span>
            </div>
            <p className="text-sm font-medium text-gray-700 signature-label">Official Seal</p>
          </div>
        </div>

        {/* Footer */}
        <div className="print-footer text-center">
          <p>This is a computer generated purchase order. Please confirm receipt and expected delivery date.</p>
          <p>AGMS | 123 Main street, Dubai, UAE | Generated on {new Date().toLocaleString()} | PO #{order.orderNo}</p>
          <p style={{fontSize: '7pt', marginTop: '2pt'}}>Continues on next page if applicable</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }
          body * { visibility: hidden; }
          .printable-content, .printable-content * { visibility: visible; }
          .printable-content { 
            position: static !important;
            width: 100% !important;
            padding: 4pt !important;
            margin-top: -8pt !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            margin-bottom: 0 !important;
            font-size: 10pt !important;
            line-height: 1.2 !important;
            background: white !important;
          }
          .no-print { display: none !important; }
          
          /* Header Layout */
          .print-header {
            margin-bottom: 12pt !important;
          }
          
          .logo-container {
            text-align: center !important;
            margin-bottom: 8pt !important;
            padding: 4pt 0 !important;
            border-bottom: 1pt solid #ddd !important;
          }
          
          .company-logo {
            height: 1.3in !important;
            width: 100% !important;
            object-fit: fill !important;
            display: block !important;
            margin: 0 auto !important;
          }
          
          .header-info {
            display: grid !important;
            grid-template-columns: 1fr 1fr 1fr !important;
            gap: 12pt !important;
            align-items: start !important;
            padding-bottom: 8pt !important;
          }
          
          .company-details h1 {
            font-size: 10pt !important;
            margin-bottom: 2pt !important;
          }
          
          .company-details p {
            font-size: 7pt !important;
          }
          
          .contact-details p {
            font-size: 7pt !important;
          }
          
          .po-details h2 {
            font-size: 14pt !important;
            margin-bottom: 4pt !important;
          }
          
          .po-details p {
            font-size: 8pt !important;
          }
          
          /* Info Boxes */
          .info-box {
            border: 1pt solid #ccc !important;
            padding: 4pt !important;
            background: #f9f9f9 !important;
            border-radius: 0 !important;
          }
          
          .info-box h3 {
            font-size: 9pt !important;
            margin-bottom: 2pt !important;
            border-bottom: 1pt solid #999 !important;
            padding-bottom: 1pt !important;
          }
          
          /* Tables */
          table { 
            width: 95% !important;
            border-collapse: collapse !important;
            margin-bottom: 10pt !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          th, td { 
            border: 1pt solid #333 !important;
            padding: 2pt 3pt !important;
            font-size: 8pt !important;
            vertical-align: middle !important;
            line-height: 1.1 !important;
          }
          th {
            background-color: #e8e8e8 !important;
            font-weight: bold !important;
            text-align: center !important;
            padding: 2pt !important;
          }
          td:nth-child(1) { text-align: center !important; width: 4% !important; }
          td:nth-child(2) { text-align: left !important; width: 25% !important; }
          td:nth-child(3) { text-align: left !important; width: 20% !important; }
          td:nth-child(4) { text-align: center !important; width: 7% !important; }
          td:nth-child(5) { text-align: right !important; width: 15% !important; }
          td:nth-child(6) { text-align: center !important; width: 8% !important; }
          td:nth-child(7) { text-align: right !important; width: 15% !important; }
          tfoot td { font-weight: bold !important; }
          tfoot td:last-child { text-align: center !important; }
          tfoot tr:last-child td { background-color: #f0f0f0 !important; }
          

          
          .section-title {
            font-size: 10pt !important;
            font-weight: bold !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5pt !important;
            margin-bottom: 4pt !important;
            border-bottom: 1pt solid #666 !important;
            padding-bottom: 2pt !important;
          }
          
          .info-grid {
            display: block !important;
          }
          
          .info-row {
            display: flex !important;
            justify-content: space-between !important;
            margin-bottom: 1pt !important;
            font-size: 8pt !important;
          }
          
          .label {
            font-weight: 500 !important;
            color: #555 !important;
            min-width: 80pt !important;
          }
          
          .value {
            text-align: right !important;
            color: #000 !important;
          }
          
          .total-row {
            border-top: 1pt solid #999 !important;
            padding-top: 2pt !important;
            margin-top: 4pt !important;
            font-weight: bold !important;
          }
          
          /* Page breaks */
          .info-box {
            page-break-inside: avoid !important;
          }
          .notes-section, .terms-section {
            page-break-inside: avoid !important;
            margin-bottom: 8pt !important;
          }
          
          .terms-section {
            font-size: 7pt !important;
          }
          
          .terms-section h3 {
            font-size: 9pt !important;
            margin-bottom: 0pt !important;
          }
          
          .terms-section p {
            font-size: 7pt !important;
            line-height: 1.2 !important;
          }
          .signature-section {
            page-break-inside: avoid !important;
            margin-top: 12pt !important;
          }
          
          /* Multi-page footer */
          .print-footer {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            text-align: center !important;
            font-size: 8pt !important;
            padding: 4pt !important;
            border-top: 1pt solid #ccc !important;
            background: white !important;
          }
          
          /* Ensure all content is visible */
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintablePurchaseOrder;